import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { zynity_api } from '../../../config/external-routes';
import './IaLimitsPage.css';

type AccessState = {
  enabled: boolean;
  reason?: string | null;
  email?: string | null;
};

type ScopeType = 'authenticated_user' | 'public_session' | 'public_site' | 'global';
type TokenSort = '' | 'desc' | 'asc';

type UsageCounter = {
  id: number;
  scope_type: string;
  scope_id: string;
  provider?: string | null;
  model?: string | null;
  interaction_mode?: string | null;
  reset_window_key?: string | null;
  total_tokens: number;
  limit_applied?: number | null;
  limit_remaining?: number | null;
  last_event_at?: string | null;
};

type UsageEvent = {
  id: number;
  status?: string | null;
  provider?: string | null;
  model?: string | null;
  interaction_mode?: string | null;
  domain_key?: string | null;
  prompt_key?: string | null;
  total_tokens: number;
  request_classification?: string | null;
  reset_window_key?: string | null;
  created_at?: string | null;
};

type UsageReport = {
  scope?: {
    scope_type: string;
    scope_id: string;
    label?: string | null;
    user?: {
      id: number;
      email?: string | null;
      name?: string | null;
    };
  };
  counters: UsageCounter[];
  recent_events: UsageEvent[];
};

type ScopeOption = {
  scope_type: ScopeType;
  scope_id: string;
  label: string;
  description?: string | null;
  user_id?: number | null;
  email?: string | null;
  public_site_origin?: string | null;
  allowed_origin?: string | null;
  usage_total_tokens?: number | null;
  usage_rolling_5h_tokens?: number | null;
  usage_limit?: {
    weekly_token_limit?: number | null;
    rolling_5h_token_limit?: number | null;
    policy_key?: string | null;
    source?: string | null;
  } | null;
};

type ScopePagination = {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
};

const scopeLabels: Record<ScopeType, string> = {
  authenticated_user: 'Authenticated User',
  public_session: 'Public Session',
  public_site: 'Public Site',
  global: 'Global',
};

const windowOptions = [
  { value: '', label: 'All active windows' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'rolling_5h', label: 'Rolling 5 hours' },
];

const tokenSortOptions: Array<{ value: TokenSort; label: string }> = [
  { value: '', label: 'Newest first' },
  { value: 'desc', label: 'Most tokens' },
  { value: 'asc', label: 'Fewest tokens' },
];

const defaultScopeInput = (scopeType: ScopeType) => {
  if (scopeType === 'global') return 'global';
  return '';
};

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined) return 'n/a';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDate = (value?: string | null) => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const windowLabel = (value?: string | null) => {
  if (!value) {
    return {
      label: 'Window n/a',
      technicalKey: '',
    };
  }

  if (value.startsWith('rolling_5h:')) {
    return {
      label: '5-hour window',
      technicalKey: value,
    };
  }

  if (value.startsWith('weekly:')) {
    return {
      label: 'Weekly window',
      technicalKey: value,
    };
  }

  return {
    label: value,
    technicalKey: '',
  };
};

const limitPercent = (counter: UsageCounter) => {
  if (!counter.limit_applied || counter.limit_applied <= 0) return 0;
  return Math.min(100, Math.round((counter.total_tokens / counter.limit_applied) * 100));
};

const tokenLimitText = (scope: ScopeOption) => {
  const used = formatNumber(scope.usage_total_tokens || 0);
  const weeklyLimit = scope.usage_limit?.weekly_token_limit;
  if (!weeklyLimit || weeklyLimit <= 0) {
    return `${used} tokens`;
  }

  return `${used} / ${formatNumber(weeklyLimit)} tokens`;
};

const rollingLimitText = (scope: ScopeOption) => {
  const rollingLimit = scope.usage_limit?.rolling_5h_token_limit;
  if (!rollingLimit || rollingLimit <= 0) {
    return '';
  }

  return `5h: ${formatNumber(scope.usage_rolling_5h_tokens || 0)} / ${formatNumber(rollingLimit)}`;
};

const counterWindowType = (counter: UsageCounter) => {
  const key = counter.reset_window_key || '';
  if (key.startsWith('rolling_5h:')) return 'rolling_5h';
  if (key.startsWith('weekly:')) return 'weekly';
  return 'other';
};

const latestCounterForType = (counters: UsageCounter[], type: 'rolling_5h' | 'weekly') => {
  return counters
    .filter((counter) => counterWindowType(counter) === type)
    .sort((a, b) => String(b.reset_window_key || '').localeCompare(String(a.reset_window_key || '')))[0] || null;
};

const usageStatus = (counter?: UsageCounter | null) => {
  if (!counter) return 'No usage';
  if (typeof counter.limit_remaining === 'number' && counter.limit_remaining <= 0) return 'Limit reached';
  return 'Allowed';
};

export const IaLimitsPage: React.FC = () => {
  const [access, setAccess] = useState<AccessState | null>(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [scopeType, setScopeType] = useState<ScopeType>('authenticated_user');
  const [search, setSearch] = useState('');
  const [tokenSort, setTokenSort] = useState<TokenSort>('');
  const [scopeId, setScopeId] = useState('');
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([]);
  const [scopePagination, setScopePagination] = useState<ScopePagination | null>(null);
  const [selectedScope, setSelectedScope] = useState<ScopeOption | null>(null);
  const [windowKey, setWindowKey] = useState('');
  const [usage, setUsage] = useState<UsageReport | null>(null);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setAccessLoading(true);
    axios
      .get(zynity_api.engagement_ai_limits_access)
      .then((response) => {
        if (!mounted) return;
        setAccess(response.data?.access || { enabled: false, reason: 'invalid_response' });
      })
      .catch(() => {
        if (!mounted) return;
        setAccess({ enabled: false, reason: 'access_check_failed' });
      })
      .finally(() => {
        if (mounted) setAccessLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const scopeHint = useMemo(() => {
    if (scopeType === 'authenticated_user') return 'Search people by name, email, or user id. Leave empty to list latest users.';
    if (scopeType === 'public_session') return 'Search public sessions by id, origin, page URL, status, or entry point. Leave empty to list latest sessions.';
    if (scopeType === 'public_site') return 'Search public sites by id, label, origin, or status. Leave empty to list latest sites.';
    return 'Global usage uses scope id "global".';
  }, [scopeType]);

  const buildScopeParams = (scopeOverride?: ScopeOption | null) => {
    const targetScope = scopeOverride || selectedScope;
    const params: Record<string, string> = { scope_type: scopeType };
    if (windowKey) params.window = windowKey;

    if (targetScope) {
      params.scope_id = targetScope.scope_id;
      return params;
    }

    params.scope_id = scopeId.trim() || defaultScopeInput(scopeType);
    return params;
  };

  const fetchScopes = async (page = 1) => {
    setLoadingScopes(true);
    setError('');
    setNotice('');
    setUsage(null);
    setSelectedScope(null);

    try {
      const params: Record<string, string | number> = {
        scope_type: scopeType,
        search: search.trim(),
        page,
        per_page: 10,
      };
      if (tokenSort) {
        params.sort_by = 'tokens';
        params.sort_direction = tokenSort;
      }

      const response = await axios.get(zynity_api.engagement_ai_limits_scopes, {
        params,
      });
      const scopes = response.data?.scopes || {};
      const items = scopes.items || [];
      setScopeOptions(items);
      setScopePagination(scopes.pagination || null);
      if (items.length === 0) {
        setNotice('No scopes found for this search.');
      }
    } catch (err: any) {
      setScopeOptions([]);
      setScopePagination(null);
      setError(err?.response?.data?.error?.message || 'Unable to load IA limit scopes.');
    } finally {
      setLoadingScopes(false);
    }
  };

  const fetchUsage = async (scopeOverride?: ScopeOption | null) => {
    setLoadingUsage(true);
    setError('');
    setNotice('');

    try {
      const response = await axios.get(zynity_api.engagement_ai_limits_usage, {
        params: buildScopeParams(scopeOverride),
      });
      setUsage(response.data?.usage || null);
    } catch (err: any) {
      setUsage(null);
      setError(err?.response?.data?.error?.message || 'Unable to load IA limits usage.');
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    fetchScopes(1);
  };

  const selectScope = (scope: ScopeOption) => {
    setSelectedScope(scope);
    setScopeId(scope.scope_id);
    setUsage(null);
    setNotice('');
    setError('');
    fetchUsage(scope);
  };

  const backToResults = () => {
    setUsage(null);
    setNotice('');
    setError('');
  };

  const handleReset = async () => {
    if (!usage?.scope) return;
    const confirmed = window.confirm('Reset the selected active usage window? Historical usage events will be preserved.');
    if (!confirmed) return;

    setResetting(true);
    setError('');
    setNotice('');

    try {
      const response = await axios.post(zynity_api.engagement_ai_limits_reset, {
        ...buildScopeParams(),
        window: windowKey || 'all',
        reason: 'Reset from /ia-limits',
      });
      setUsage(response.data?.usage || null);
      const resetCount = response.data?.reset?.reset_count ?? 0;
      const previousTotal = response.data?.reset?.previous_total_tokens ?? 0;
      setNotice(`Reset complete. Windows reset: ${resetCount}. Previous total tokens: ${formatNumber(previousTotal)}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Unable to reset IA limits usage.');
    } finally {
      setResetting(false);
    }
  };

  const handleScopeChange = (nextScopeType: ScopeType) => {
    setScopeType(nextScopeType);
    setUsage(null);
    setNotice('');
    setError('');
    setSearch('');
    setTokenSort('');
    setScopeId(defaultScopeInput(nextScopeType));
    setSelectedScope(null);
    setScopeOptions([]);
    setScopePagination(null);
  };

  const weeklyCounter = usage ? latestCounterForType(usage.counters, 'weekly') : null;
  const rollingCounter = usage ? latestCounterForType(usage.counters, 'rolling_5h') : null;
  const currentCounters = usage
    ? usage.counters.filter((counter) => {
      const type = counterWindowType(counter);
      if (type === 'weekly') return weeklyCounter?.id === counter.id;
      if (type === 'rolling_5h') return rollingCounter?.id === counter.id;
      return true;
    })
    : [];

  if (accessLoading) {
    return (
      <main className="ia-limits-page">
        <section className="ia-limits-card ia-limits-card--compact">Checking IA Limits access...</section>
      </main>
    );
  }

  if (!access?.enabled) {
    return (
      <main className="ia-limits-page">
        <section className="ia-limits-card ia-limits-card--compact">
          <p className="ia-limits-eyebrow">IA Limits</p>
          <h1>Access denied</h1>
          <p>This internal page is available only to approved Zynity support users.</p>
          <p className="ia-limits-muted">Reason: {access?.reason || 'not_allowed'}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="ia-limits-page">
      <section className="ia-limits-hero">
        <div>
          <p className="ia-limits-eyebrow">Zyni Operations</p>
          <h1>IA Limits</h1>
          <p>Inspect token usage and reset active windows for authenticated users and public Explore usage.</p>
        </div>
        <div className="ia-limits-access-pill">Access: {access.email || 'approved'}</div>
      </section>

      <section className="ia-limits-card">
        <form className="ia-limits-form" onSubmit={handleSubmit}>
          <div className="ia-limits-form-grid">
            <div className="ia-limits-field ia-limits-field--scope">
              <label htmlFor="ia-limits-scope">Scope</label>
              <select id="ia-limits-scope" value={scopeType} onChange={(event) => handleScopeChange(event.target.value as ScopeType)}>
                {Object.entries(scopeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <small>{scopeHint}</small>
            </div>

            <div className="ia-limits-field ia-limits-field--search">
              <label htmlFor="ia-limits-search">Search Filter</label>
              <input id="ia-limits-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={scopeType === 'authenticated_user' ? 'Name, email, or user id' : 'ID, origin, label, URL, or status'} />
            </div>

            <div className="ia-limits-field ia-limits-field--sort">
              <label htmlFor="ia-limits-token-sort">Sort</label>
              <select id="ia-limits-token-sort" value={tokenSort} onChange={(event) => setTokenSort(event.target.value as TokenSort)}>
                {tokenSortOptions.map((option) => (
                  <option key={option.value || 'default'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="ia-limits-field ia-limits-field--window">
              <label htmlFor="ia-limits-window">Window</label>
              <select id="ia-limits-window" value={windowKey} onChange={(event) => setWindowKey(event.target.value)}>
                {windowOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="ia-limits-form-bottom">
              <div className="ia-limits-form-actions">
                <button className="ia-limits-secondary" type="button" disabled={loadingScopes} onClick={() => fetchScopes(1)}>
                  {loadingScopes ? 'Searching...' : 'Search Scopes'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </section>

      {loadingUsage && <div className="ia-limits-alert ia-limits-alert--success">Loading selected scope usage...</div>}

      {!usage && scopeOptions.length > 0 && (
        <section className="ia-limits-card ia-limits-scope-list">
          <div className="ia-limits-scope-list-header">
            <div>
              <p className="ia-limits-eyebrow">Search Results</p>
              <h2>{scopeLabels[scopeType]}</h2>
            </div>
            {scopePagination && <span>{scopePagination.total} total</span>}
          </div>
          <div className="ia-limits-scope-options">
            {scopeOptions.map((scope) => (
              <button
                type="button"
                key={`${scope.scope_type}:${scope.scope_id}`}
                className={`ia-limits-scope-option ${selectedScope?.scope_id === scope.scope_id ? 'is-selected' : ''}`}
                onClick={() => selectScope(scope)}
              >
                <span>
                  <strong>{scope.label}</strong>
                  <small>{scope.description || `${scope.scope_type}:${scope.scope_id}`}</small>
                </span>
                <em>
                  <strong>{tokenLimitText(scope)}</strong>
                  {rollingLimitText(scope) && <small>{rollingLimitText(scope)}</small>}
                </em>
              </button>
            ))}
          </div>
          {scopePagination && scopePagination.last_page > 1 && (
            <div className="ia-limits-pagination">
              <button type="button" disabled={scopePagination.page <= 1 || loadingScopes} onClick={() => fetchScopes(scopePagination.page - 1)}>Previous</button>
              <span>Page {scopePagination.page} of {scopePagination.last_page}</span>
              <button type="button" disabled={scopePagination.page >= scopePagination.last_page || loadingScopes} onClick={() => fetchScopes(scopePagination.page + 1)}>Next</button>
            </div>
          )}
        </section>
      )}

      {error && <div className="ia-limits-alert ia-limits-alert--error">{error}</div>}
      {notice && <div className="ia-limits-alert ia-limits-alert--success">{notice}</div>}

      {usage && (
        <section className="ia-limits-results">
          <div className="ia-limits-detail-header ia-limits-card">
            <div>
              <button className="ia-limits-back" type="button" onClick={backToResults}>
                Back to Search Results
              </button>
            </div>
            <div className="ia-limits-detail-title">
              <p className="ia-limits-eyebrow">Selected Scope</p>
              <h2>{usage.scope?.label || `${usage.scope?.scope_type}:${usage.scope?.scope_id}`}</h2>
              {usage.scope?.user && <p className="ia-limits-muted">{usage.scope.user.name} · {usage.scope.user.email}</p>}
            </div>
            <button className="ia-limits-danger" type="button" onClick={handleReset} disabled={resetting}>
              {resetting ? 'Resetting...' : 'Reset Active Window'}
            </button>
          </div>

          <div className="ia-limits-summary-grid">
            <article className="ia-limits-summary-card">
              <span>Weekly</span>
              <strong>{weeklyCounter ? `${formatNumber(weeklyCounter.total_tokens)} / ${formatNumber(weeklyCounter.limit_applied)}` : 'No usage'}</strong>
              <small>Status: {usageStatus(weeklyCounter)}</small>
              <small>Last event: {formatDate(weeklyCounter?.last_event_at)}</small>
            </article>
            <article className="ia-limits-summary-card">
              <span>5-hour</span>
              <strong>{rollingCounter ? `${formatNumber(rollingCounter.total_tokens)} / ${formatNumber(rollingCounter.limit_applied)}` : 'No usage'}</strong>
              <small>Status: {usageStatus(rollingCounter)}</small>
              <small>Last event: {formatDate(rollingCounter?.last_event_at)}</small>
            </article>
          </div>

          <div className="ia-limits-counter-grid">
            {currentCounters.length === 0 ? (
              <div className="ia-limits-empty">No active counters found for this scope.</div>
            ) : currentCounters.map((counter) => (
              <article className="ia-limits-counter" key={counter.id}>
                <div className="ia-limits-counter-top">
                  <span className="ia-limits-window-title">
                    <strong>{windowLabel(counter.reset_window_key).label}</strong>
                    {windowLabel(counter.reset_window_key).technicalKey && <small>{windowLabel(counter.reset_window_key).technicalKey}</small>}
                  </span>
                  <span>{counter.interaction_mode || 'mode n/a'}</span>
                </div>
                <div className="ia-limits-meter" aria-label={`Usage ${limitPercent(counter)} percent`}>
                  <span style={{ width: `${limitPercent(counter)}%` }} />
                </div>
                <dl>
                  <div><dt>Used</dt><dd>{formatNumber(counter.total_tokens)}</dd></div>
                  <div><dt>Limit</dt><dd>{formatNumber(counter.limit_applied)}</dd></div>
                  <div><dt>Remaining</dt><dd>{formatNumber(counter.limit_remaining)}</dd></div>
                  <div><dt>Provider</dt><dd>{counter.provider || 'n/a'} / {counter.model || 'n/a'}</dd></div>
                  <div><dt>Last Event</dt><dd>{formatDate(counter.last_event_at)}</dd></div>
                </dl>
              </article>
            ))}
          </div>

          <div className="ia-limits-card ia-limits-events-card">
            <h3>Recent Usage Events</h3>
            {usage.recent_events.length === 0 ? (
              <p className="ia-limits-muted">No recent events found.</p>
            ) : (
              <div className="ia-limits-table-wrap">
                <table className="ia-limits-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Tokens</th>
                      <th>Window</th>
                      <th>Classification</th>
                      <th>Prompt</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.recent_events.map((event) => (
                      <tr key={event.id}>
                        <td>{event.status || 'n/a'}</td>
                        <td>{formatNumber(event.total_tokens)}</td>
                        <td>
                          <span className="ia-limits-window-title ia-limits-window-title--table">
                            <strong>{windowLabel(event.reset_window_key).label}</strong>
                            {windowLabel(event.reset_window_key).technicalKey && <small>{windowLabel(event.reset_window_key).technicalKey}</small>}
                          </span>
                        </td>
                        <td>{event.request_classification || 'n/a'}</td>
                        <td>{event.prompt_key || event.domain_key || 'n/a'}</td>
                        <td>{formatDate(event.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </main>
  );
};
